import { useEffect, useMemo } from 'react';
import type { Column, Table, Row } from '@tanstack/react-table';

import type { Option } from '../TableFilterSelect';
import TableFilter, { type TableFilterProps } from '../TableFilter';

type FilterComponentProps<T, OptionValue extends string | number> = {
  column: Column<T, unknown>;
  table: Table<T>;
  selectAllByDefault?: boolean;
} & Pick<
  TableFilterProps<OptionValue>,
  'selectAllLabel' | 'showSelectAllOption'
> &
  (
    | { options: Option<OptionValue>[]; selectOptions?: undefined }
    | {
        options?: undefined;
        selectOptions: (flatRows: Row<T>[]) => Option<OptionValue>[];
      }
  );

const FilterComponent = <Value, T extends string | number>(
  props: FilterComponentProps<Value, T>
) => {
  const {
    column,
    options: propOptions,
    table,
    selectOptions,
    selectAllLabel,
    selectAllByDefault = false,
    showSelectAllOption,
  } = props;

  const options = useMemo(
    () => propOptions ?? selectOptions(table.getPreFilteredRowModel().flatRows),
    [table, propOptions]
  );

  const value = column.getFilterValue() as (string | number)[];
  const selectedAll = options.length === value?.length;

  useEffect(() => {
    if (selectAllByDefault) {
      const values = options.map((option) => option.value);
      column.setFilterValue(values);
    }
  }, [column, options, selectAllByDefault]);

  return (
    <TableFilter
      selectedAll={selectedAll}
      showSelectAllOption={showSelectAllOption}
      selectAllLabel={selectAllLabel}
      value={value}
      options={options}
      onChange={(newValues) => {
        column.setFilterValue(newValues);
      }}
    />
  );
};

export type { Option };
export default FilterComponent;
